import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Building2,
  Users,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Shield,
  Globe,
  BarChart3,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  AlertTriangle,
  Crown,
  Layers,
  ClipboardList,
  Activity,
  Database,
  RefreshCw,
  User,
  Mail,
  Key,
  Save,
  Play,
  Lock,
  Unlock
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE, BACKEND_URL } from '../utils/apiClient';

const API = API_BASE;

const SuperAdmin = () => {
  const { getAuthHeaders, user, updateUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [saving, setSaving] = useState(false);

  // Profile editing state
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Create tenant form
  const [createForm, setCreateForm] = useState({
    name: '',
    subdomain: '',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    primary_color: '#0E346E',
    timezone: 'America/Montreal',
    language: 'fr'
  });

  // Demo management state
  const [demoStats, setDemoStats] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Initialize profile form when user data is available
  useEffect(() => {
    if (user) {
      setProfileForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const config = { headers: getAuthHeaders(), timeout: 15000 };
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/tenants`, config),
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/migration-status`, config)
      ]);
      
      if (results[0].status === 'fulfilled') {
        setTenants(results[0].value?.data || []);
      }
      if (results[1].status === 'fulfilled') {
        setStats(results[1].value?.data || null);
      }
      if (results[2].status === 'fulfilled') {
        setMigrationStatus(results[2].value?.data || null);
      }
      
      const hasError = results.some(r => r.status === 'rejected' && r.reason?.response?.status === 403);
      if (hasError) {
        toast.error('Acces non autorise - Super Admin requis');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleRunMigration = async () => {
    try {
      setSaving(true);
      const response = await axios.post(`${API}/admin/migrate-tenant-data`, {}, { headers: getAuthHeaders() });
      toast.success('Migration terminée avec succès');
      // Refresh migration status
      const migrationRes = await axios.get(`${API}/admin/migration-status`, { headers: getAuthHeaders() });
      setMigrationStatus(migrationRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la migration');
    } finally {
      setSaving(false);
    }
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    try {
      setSavingProfile(true);
      
      // Validate passwords if changing
      if (profileForm.newPassword) {
        if (!profileForm.currentPassword) {
          toast.error('Veuillez entrer votre mot de passe actuel');
          return;
        }
        if (profileForm.newPassword !== profileForm.confirmPassword) {
          toast.error('Les nouveaux mots de passe ne correspondent pas');
          return;
        }
        if (profileForm.newPassword.length < 8) {
          toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères');
          return;
        }
      }

      const updateData = {
        name: profileForm.name,
        email: profileForm.email
      };

      // Add password change if provided
      if (profileForm.newPassword && profileForm.currentPassword) {
        updateData.current_password = profileForm.currentPassword;
        updateData.new_password = profileForm.newPassword;
      }

      const response = await axios.put(
        `${API}/users/me/profile`,
        updateData,
        { headers: getAuthHeaders() }
      );

      toast.success('Profil mis à jour avec succès');
      
      // Update local user context if function exists
      if (updateUser) {
        updateUser(response.data);
      }

      // Clear password fields
      setProfileForm(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la mise à jour';
      toast.error(message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleRunDiagnostics = async () => {
    try {
      setSaving(true);
      const response = await axios.get(`${API}/admin/diagnostics`, { headers: getAuthHeaders() });
      if (response.data.status === 'healthy') {
        toast.success(`✅ Diagnostic OK - ${response.data.stats.tenants} tenant(s), ${response.data.stats.users} utilisateur(s)`);
      } else {
        toast.error(`⚠️ ${response.data.issues_count} problème(s) détecté(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du diagnostic');
    } finally {
      setSaving(false);
    }
  };

  const handleFixOrphanData = async () => {
    try {
      setSaving(true);
      const response = await axios.post(`${API}/admin/fix-orphan-data`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
    } finally {
      setSaving(false);
    }
  };

  const handlePopulateChecklists = async () => {
    try {
      setSaving(true);
      const response = await axios.post(`${API}/admin/populate-empty-checklists`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      if (response.data.populated_count > 0) {
        toast.info(`${response.data.populated_count} checklist(s) mise(s) à jour avec des items`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du peuplement des checklists');
    } finally {
      setSaving(false);
    }
  };

  const handleCleanupTestData = async () => {
    if (!window.confirm('⚠️ Cette action va supprimer toutes les zones de test (Production Line A, Ligne de Production A, etc.) et leurs données associées. Continuer ?')) {
      return;
    }
    try {
      setSaving(true);
      const response = await axios.post(`${API}/admin/cleanup-test-data`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      if (response.data.details?.deleted_zone_names?.length > 0) {
        toast.info(`Zones supprimees: ${response.data.details.deleted_zone_names.join(', ')}`);
      }
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du nettoyage');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!createForm.name || !createForm.subdomain || !createForm.admin_email || !createForm.admin_name || !createForm.admin_password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/tenants`, createForm, { headers: getAuthHeaders() });
      toast.success(`Client "${createForm.name}" créé avec succès`);
      setCreateDialogOpen(false);
      setCreateForm({
        name: '',
        subdomain: '',
        admin_email: '',
        admin_name: '',
        admin_password: '',
        primary_color: '#0E346E',
        timezone: 'America/Montreal',
        language: 'fr'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleViewTenant = async (tenant) => {
    try {
      const response = await axios.get(`${API}/admin/tenants/${tenant.id}`, { headers: getAuthHeaders() });
      setSelectedTenant(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleStatusChange = async (tenantId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/tenants/${tenantId}/status?status=${newStatus}`, {}, { headers: getAuthHeaders() });
      toast.success(`Statut mis à jour: ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    
    try {
      await axios.delete(`${API}/admin/tenants/${selectedTenant.id}?confirm=true`, { headers: getAuthHeaders() });
      toast.success(`Client "${selectedTenant.name}" supprimé`);
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Demo management handlers
  const fetchDemoStats = async () => {
    try {
      setDemoLoading(true);
      const res = await axios.get(`${API}/admin/demo/stats`, { headers: getAuthHeaders() });
      setDemoStats(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des stats démo');
    } finally {
      setDemoLoading(false);
    }
  };

  const handleRegenerateDemo = async () => {
    if (!window.confirm('Cela va supprimer et régénérer TOUTES les données de démonstration. Continuer ?')) return;
    try {
      setRegenerating(true);
      const res = await axios.post(`${API}/admin/demo/regenerate`, {}, { headers: getAuthHeaders() });
      if (res.data.success) {
        toast.success('Données de démonstration régénérées avec succès !');
        fetchDemoStats();
      } else {
        toast.error('Erreur : ' + (res.data.error || 'Inconnue'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la régénération');
    } finally {
      setRegenerating(false);
    }
  };

  const handleResetDemoPassword = async () => {
    try {
      await axios.post(`${API}/admin/demo/reset-password`, {}, { headers: getAuthHeaders() });
      toast.success('Mot de passe réinitialisé à Demo123!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleReadOnly = async (readOnly) => {
    try {
      await axios.put(`${API}/admin/demo/toggle-readonly`, { read_only: readOnly }, { headers: getAuthHeaders() });
      toast.success(readOnly ? 'Lecture seule activée' : 'Lecture seule désactivée');
      fetchDemoStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      trial: 'bg-amber-100 text-amber-800'
    };
    const labels = {
      active: 'Actif',
      suspended: 'Suspendu',
      trial: 'Essai'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  const getTierBadge = (tier) => {
    const styles = {
      free: 'bg-slate-100 text-slate-800',
      starter: 'bg-blue-100 text-blue-800',
      professional: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-amber-100 text-amber-800'
    };
    return <Badge className={styles[tier] || 'bg-slate-100'}>{tier}</Badge>;
  };

  if (!user?.is_super_admin) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900">Accès Refusé</h1>
        <p className="text-slate-500 mt-2">Cette page est réservée au Super Administrateur.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="super-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Crown className="w-8 h-8 text-amber-500" />
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
              SUPER ADMIN
            </h1>
          </div>
          <p className="text-slate-500 mt-1">Gestion de la plateforme multi-client</p>
        </div>
        <Button 
          onClick={() => setCreateDialogOpen(true)} 
          className="bg-[#0E346E] hover:bg-[#0A2850]"
          data-testid="create-tenant-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Client
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'demo') fetchDemoStats(); }}>
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Tableau de bord</span>
            <span className="sm:hidden">Stats</span>
          </TabsTrigger>
          <TabsTrigger value="tenants" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Clients ({tenants.length})</span>
            <span className="sm:hidden">Clients</span>
          </TabsTrigger>
          <TabsTrigger value="demo" className="flex items-center gap-2" data-testid="demo-tab">
            <Play className="w-4 h-4" />
            Démo
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Données
          </TabsTrigger>
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Mon Profil</span>
            <span className="sm:hidden">Profil</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-6">
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-slate-300" />
              <p>Chargement des statistiques...</p>
            </div>
          ) : stats ? (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats?.tenants_count || 0}</p>
                        <p className="text-sm text-slate-500">Clients</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats?.tenants_active || 0}</p>
                        <p className="text-sm text-slate-500">Actifs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-purple-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                        <p className="text-sm text-slate-500">Utilisateurs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-amber-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Activity className="w-8 h-8 text-amber-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats?.total_tours || 0}</p>
                        <p className="text-sm text-slate-500">Tournees</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <Layers className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                      <p className="text-xl font-bold">{stats?.total_sites || 0}</p>
                      <p className="text-xs text-slate-500">Sites/Unites</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <ClipboardList className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                      <p className="text-xl font-bold">{stats?.total_checklists || 0}</p>
                      <p className="text-xs text-slate-500">Checklists</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <AlertTriangle className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                      <p className="text-xl font-bold">{stats?.total_actions || 0}</p>
                      <p className="text-xs text-slate-500">Actions</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <Clock className="w-6 h-6 text-red-400 mx-auto mb-1" />
                      <p className="text-xl font-bold text-red-600">{stats?.actions_open || 0}</p>
                      <p className="text-xs text-slate-500">Actions ouvertes</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Tenants */}
              {Array.isArray(stats?.recent_tenants) && stats.recent_tenants.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-500" />
                      Clients récents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {stats.recent_tenants.map(tenant => (
                        <div key={tenant.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="font-medium">{tenant.name}</p>
                              <p className="text-xs text-slate-500">{tenant.subdomain}.tmbsaas.com</p>
                            </div>
                          </div>
                          {getStatusBadge(tenant.status)}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <p className="text-slate-700 font-medium">Impossible de charger les statistiques</p>
              <p className="text-sm text-slate-500 mt-2">Verifiez votre connexion ou les permissions Super Admin.</p>
              <Button variant="outline" className="mt-4" onClick={() => { setLoading(true); fetchData(); }}>
                <RefreshCw className="w-4 h-4 mr-2" /> Reessayer
              </Button>
            </div>
          )}
        </TabsContent>
        <TabsContent value="tenants" className="mt-6">
          {tenants.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t('superAdmin.noClients')}</p>
                <p className="text-sm text-slate-400 mt-1">{t('superAdmin.createFirstClient')}</p>
                <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('superAdmin.newClient')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tenants.map(tenant => (
                <Card key={tenant.id} className="hover:shadow-md transition-shadow" data-testid={`tenant-card-${tenant.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {tenant.logo_url ? (
                          <img 
                            src={`${BACKEND_URL}${tenant.logo_url}`} 
                            alt={tenant.name}
                            className="w-10 h-10 object-contain rounded"
                          />
                        ) : (
                          <div 
                            className="w-10 h-10 rounded flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: tenant.primary_color || '#0E346E' }}
                          >
                            {tenant.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <CardTitle className="text-lg">{tenant.name}</CardTitle>
                          <p className="text-xs text-slate-500">{tenant.subdomain}.tmbsaas.com</p>
                        </div>
                      </div>
                      {getStatusBadge(tenant.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{tenant.users_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Layers className="w-4 h-4" />
                        <span>{tenant.sites_count || 0}</span>
                      </div>
                      {getTierBadge(tenant.subscription_tier)}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="flex-1 bg-[#0E346E] hover:bg-[#0A2850]"
                        onClick={() => handleViewTenant(tenant)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Détails
                      </Button>
                      {tenant.status === 'active' ? (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-amber-600"
                          onClick={() => handleStatusChange(tenant.id, 'suspended')}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-green-600"
                          onClick={() => handleStatusChange(tenant.id, 'active')}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600"
                        onClick={() => {
                          setSelectedTenant(tenant);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Data Management Tab */}
        <TabsContent value="data" className="mt-6">
          <div className="space-y-6">
            {/* Migration Status Card */}
            <Card className="border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-amber-600" />
                  État de l'isolation des données
                </CardTitle>
                <CardDescription>
                  Vérifiez l'état de l'isolation multi-tenant des données
                </CardDescription>
              </CardHeader>
              <CardContent>
                {migrationStatus && (
                  <div className="space-y-4">
                    {/* Migration needed warning */}
                    {migrationStatus?.needs_migration && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-amber-800">Migration requise</h4>
                            <p className="text-sm text-amber-700 mt-1">
                              Des donnees sans tenant_id ont ete detectees. Cliquez sur "Migrer les donnees" pour les assigner a la plateforme par defaut.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Status table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-semibold">Collection</th>
                            <th className="text-center p-3 font-semibold">Total</th>
                            <th className="text-center p-3 font-semibold">Sans tenant</th>
                            <th className="text-center p-3 font-semibold">Plateforme</th>
                            <th className="text-center p-3 font-semibold">Avec tenant</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(migrationStatus?.status || {}).map(([collection, data]) => (
                            <tr key={collection} className="border-b hover:bg-slate-50">
                              <td className="p-3 font-medium capitalize">{collection}</td>
                              <td className="text-center p-3">{data?.total || 0}</td>
                              <td className="text-center p-3">
                                {(data?.without_tenant || 0) > 0 ? (
                                  <Badge className="bg-red-100 text-red-800">{data?.without_tenant || 0}</Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-800">0</Badge>
                                )}
                              </td>
                              <td className="text-center p-3">
                                <Badge className="bg-blue-100 text-blue-800">{data?.platform_default || 0}</Badge>
                              </td>
                              <td className="text-center p-3">
                                <Badge className="bg-purple-100 text-purple-800">{data?.with_tenant || 0}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Migration button */}
                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={fetchData}
                        disabled={saving}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                        Actualiser
                      </Button>
                      {migrationStatus?.needs_migration && (
                        <Button
                          onClick={handleRunMigration}
                          disabled={saving}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Database className="w-4 h-4 mr-2" />
                          )}
                          Migrer les donnees
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Legend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Légende</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-800">Sans tenant</Badge>
                    <span className="text-slate-600">Données orphelines (à migrer)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-800">Plateforme</Badge>
                    <span className="text-slate-600">Données de la plateforme (visibles par Super Admin)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-800">Avec tenant</Badge>
                    <span className="text-slate-600">Données assignées à un client spécifique</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-800">0</Badge>
                    <span className="text-slate-600">Aucune donnée orpheline</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Maintenance Tools */}
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-600" />
                  Outils de maintenance
                </CardTitle>
                <CardDescription>
                  Outils pour diagnostiquer et corriger les problèmes de données
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Diagnostic Button */}
                  <div className="p-4 border rounded-lg bg-slate-50">
                    <h4 className="font-semibold text-slate-700 mb-2">Diagnostic Multi-Tenant</h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Vérifie l'intégrité des données et détecte les incohérences.
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleRunDiagnostics}
                      disabled={saving}
                      className="w-full"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Lancer le diagnostic
                    </Button>
                  </div>

                  {/* Fix Orphan Data Button */}
                  <div className="p-4 border rounded-lg bg-slate-50">
                    <h4 className="font-semibold text-slate-700 mb-2">Corriger données orphelines</h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Assigne les données sans tenant_id au tenant actif.
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleFixOrphanData}
                      disabled={saving}
                      className="w-full"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Database className="w-4 h-4 mr-2" />
                      )}
                      Corriger les données
                    </Button>
                  </div>

                  {/* Populate Empty Checklists Button */}
                  <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                    <h4 className="font-semibold text-amber-800 mb-2">Peupler checklists vides</h4>
                    <p className="text-sm text-amber-700 mb-3">
                      Ajoute des items standards aux checklists vides (5S, Sécurité, etc.)
                    </p>
                    <Button
                      onClick={handlePopulateChecklists}
                      disabled={saving}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ClipboardList className="w-4 h-4 mr-2" />
                      )}
                      Peupler les checklists
                    </Button>
                  </div>

                  {/* Cleanup Test Data Button */}
                  <div className="p-4 border rounded-lg bg-red-50 border-red-200">
                    <h4 className="font-semibold text-red-800 mb-2">Nettoyer données de test</h4>
                    <p className="text-sm text-red-700 mb-3">
                      Supprime les zones avec des noms génériques (Production Line A, etc.)
                    </p>
                    <Button
                      onClick={handleCleanupTestData}
                      disabled={saving}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Nettoyer les données de test
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6">
          <div className="max-w-2xl">
            <Card>
              <CardHeader className="border-b bg-slate-50">
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  Mon Profil Super Admin
                </CardTitle>
                <CardDescription>
                  Gérez vos informations personnelles et votre mot de passe
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Informations personnelles
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="profile-name">Nom complet</Label>
                      <Input
                        id="profile-name"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Votre nom"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="profile-email">Adresse email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="profile-email"
                          type="email"
                          value={profileForm.email}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="votre@email.com"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-4">
                    <Key className="w-4 h-4" />
                    Changer le mot de passe
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Laissez ces champs vides si vous ne souhaitez pas changer votre mot de passe.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password">Mot de passe actuel</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={profileForm.currentPassword}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                        placeholder="••••••••"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-password">Nouveau mot de passe</Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={profileForm.newPassword}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, newPassword: e.target.value }))}
                          placeholder="••••••••"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={profileForm.confirmPassword}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current User Info */}
                <div className="bg-slate-50 rounded-lg p-4 border">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Informations du compte</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">ID :</span>
                      <span className="ml-2 font-mono text-xs">{user?.id?.slice(0, 8)}...</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Rôle :</span>
                      <Badge className="ml-2 bg-amber-100 text-amber-800">Super Admin</Badge>
                    </div>
                    <div>
                      <span className="text-slate-500">Email actuel :</span>
                      <span className="ml-2">{user?.email}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Créé le :</span>
                      <span className="ml-2">{user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={handleUpdateProfile}
                    disabled={savingProfile}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Enregistrer les modifications
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Demo Management Tab */}
        <TabsContent value="demo" className="mt-6" data-testid="demo-tab-content">
          <div className="space-y-6">
            {/* Demo Header */}
            <Card className="border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Barlow_Condensed'] text-xl">
                  <Play className="w-5 h-5 text-amber-500" />
                  ENVIRONNEMENT DE DÉMONSTRATION
                </CardTitle>
                <CardDescription>
                  Gérez l'environnement de démo pour les présentations commerciales
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleRegenerateDemo}
                    disabled={regenerating}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="regenerate-demo-btn"
                  >
                    {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {regenerating ? 'Régénération en cours...' : 'Régénérer les données'}
                  </Button>
                  <Button
                    onClick={handleResetDemoPassword}
                    variant="outline"
                    data-testid="reset-demo-password-btn"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Réinitialiser le mot de passe
                  </Button>
                  <Button
                    onClick={fetchDemoStats}
                    variant="outline"
                    disabled={demoLoading}
                    data-testid="refresh-demo-stats-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${demoLoading ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Access Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  Accès Démo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Email</p>
                    <p className="font-mono font-bold text-slate-900">demo@gembacoach.com</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Mot de passe</p>
                    <p className="font-mono font-bold text-slate-900">Demo123!</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Read-Only Toggle */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  {demoStats?.demo_read_only ? (
                    <Lock className="w-4 h-4 text-green-600" />
                  ) : (
                    <Unlock className="w-4 h-4 text-red-500" />
                  )}
                  Mode Lecture Seule
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">
                      {demoStats?.demo_read_only ? 'Activé — Les prospects ne peuvent rien modifier' : 'Désactivé — Attention : les prospects peuvent modifier les données !'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Ce paramètre bloque toutes les opérations d'écriture pour les utilisateurs du tenant démo
                    </p>
                  </div>
                  <Button
                    onClick={() => handleToggleReadOnly(!demoStats?.demo_read_only)}
                    variant={demoStats?.demo_read_only ? 'outline' : 'default'}
                    className={!demoStats?.demo_read_only ? 'bg-green-600 hover:bg-green-700' : ''}
                    data-testid="toggle-readonly-btn"
                  >
                    {demoStats?.demo_read_only ? (
                      <><Unlock className="w-4 h-4 mr-2" />Désactiver</>
                    ) : (
                      <><Lock className="w-4 h-4 mr-2" />Activer</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Demo Stats */}
            {demoStats?.exists && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-purple-500" />
                    Données de démonstration
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: 'Utilisateurs', value: demoStats.users, color: 'blue' },
                      { label: 'Tournées', value: demoStats.tours?.toLocaleString(), color: 'green' },
                      { label: 'Actions', value: demoStats.actions?.toLocaleString(), color: 'amber' },
                      { label: 'Entrées TRS', value: demoStats.trs_entries?.toLocaleString(), color: 'purple' },
                      { label: 'Incidents SST', value: demoStats.safety_incidents, color: 'red' },
                      { label: 'Checklists', value: demoStats.checklists, color: 'teal' },
                      { label: 'Sites', value: demoStats.sites, color: 'indigo' },
                      { label: 'Zones', value: demoStats.zones, color: 'orange' },
                    ].map((stat) => (
                      <div key={stat.label} className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-slate-900">{stat.value || 0}</p>
                        <p className="text-xs text-slate-500 mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {!demoStats?.exists && !demoLoading && (
              <Card className="border-dashed border-2">
                <CardContent className="py-12 text-center">
                  <Database className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 text-lg mb-4">Aucun environnement de démo trouvé</p>
                  <Button
                    onClick={handleRegenerateDemo}
                    disabled={regenerating}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                    Créer l'environnement de démo
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Tenant Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              NOUVEAU CLIENT
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <p className="font-medium mb-1">Configuration initiale</p>
              <p>Un compte administrateur sera créé pour ce client avec les identifiants fournis.</p>
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">Nom de l'entreprise *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                placeholder="Ex: Geotrack Industries"
                className="border-2"
                data-testid="tenant-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">Sous-domaine *</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={createForm.subdomain}
                  onChange={(e) => setCreateForm({...createForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                  placeholder="geotrack"
                  className="border-2"
                  data-testid="tenant-subdomain-input"
                />
                <span className="text-sm text-slate-500 whitespace-nowrap">.tmbsaas.com</span>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold text-slate-700 mb-3">Administrateur du client</h4>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Nom de l'admin *</Label>
                  <Input
                    value={createForm.admin_name}
                    onChange={(e) => setCreateForm({...createForm, admin_name: e.target.value})}
                    placeholder="Jean Dupont"
                    className="border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email de l'admin *</Label>
                  <Input
                    type="email"
                    value={createForm.admin_email}
                    onChange={(e) => setCreateForm({...createForm, admin_email: e.target.value})}
                    placeholder="admin@geotrack.com"
                    className="border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Mot de passe initial *</Label>
                  <Input
                    type="password"
                    value={createForm.admin_password}
                    onChange={(e) => setCreateForm({...createForm, admin_password: e.target.value})}
                    placeholder="Minimum 8 caractères"
                    className="border-2"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold text-slate-700 mb-3">Personnalisation (optionnel)</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Couleur principale</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={createForm.primary_color}
                      onChange={(e) => setCreateForm({...createForm, primary_color: e.target.value})}
                      className="w-10 h-10 rounded cursor-pointer border-2"
                    />
                    <Input
                      value={createForm.primary_color}
                      onChange={(e) => setCreateForm({...createForm, primary_color: e.target.value})}
                      className="border-2 font-mono"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Fuseau horaire</Label>
                  <select
                    value={createForm.timezone}
                    onChange={(e) => setCreateForm({...createForm, timezone: e.target.value})}
                    className="w-full h-10 px-3 border-2 rounded-md"
                  >
                    <option value="America/Montreal">America/Montréal</option>
                    <option value="America/Toronto">America/Toronto</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="Europe/Paris">Europe/Paris</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Africa/Casablanca">Africa/Casablanca</option>
                    <option value="Africa/Dakar">Africa/Dakar</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button 
              onClick={handleCreateTenant} 
              disabled={saving || !createForm.name || !createForm.subdomain || !createForm.admin_email || !createForm.admin_password}
              className="bg-[#0E346E] hover:bg-[#0A2850]"
              data-testid="save-tenant-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {t('superAdmin.createClientBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Tenant Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              {t('superAdmin.clientDetails')}
            </DialogTitle>
          </DialogHeader>

          {selectedTenant && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-16 rounded-lg flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: selectedTenant.primary_color || '#0E346E' }}
                >
                  {selectedTenant.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedTenant.name}</h3>
                  <p className="text-slate-500">{selectedTenant.subdomain}.tmbsaas.com</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusBadge(selectedTenant.status)}
                    {getTierBadge(selectedTenant.subscription_tier)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{selectedTenant.users_count}</p>
                  <p className="text-xs text-slate-500">{t('superAdmin.users')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{selectedTenant.sites_count}</p>
                  <p className="text-xs text-slate-500">{t('superAdmin.sitesUnits')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{selectedTenant.checklists_count || 0}</p>
                  <p className="text-xs text-slate-500">{t('nav.checklists')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{selectedTenant.tours_count || 0}</p>
                  <p className="text-xs text-slate-500">{t('superAdmin.tours')}</p>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('superAdmin.timezone')}</span>
                  <span>{selectedTenant.timezone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('common.language')}</span>
                  <span>{selectedTenant.language === 'fr' ? 'Français' : selectedTenant.language}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('superAdmin.createdOn')}</span>
                  <span>{new Date(selectedTenant.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {t('superAdmin.deleteClient')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>{t('superAdmin.deleteConfirm')} <strong>"{selectedTenant?.name}"</strong>.</p>
                <div className="bg-red-50 p-3 rounded-lg text-sm text-red-800">
                  <p className="font-medium mb-1">{t('superAdmin.deleteWarning')}</p>
                  <ul className="list-disc list-inside space-y-1 text-red-700">
                    <li>{t('superAdmin.deleteDetails.users')}</li>
                    <li>{t('superAdmin.deleteDetails.sites')}</li>
                    <li>{t('superAdmin.deleteDetails.history')}</li>
                    <li>{t('superAdmin.deleteDetails.data')}</li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTenant}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {t('superAdmin.deleteForever')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SuperAdmin;
